import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useGroups } from '@communities/groups-layer/composables/useGroups'

import { useChannelsQueryStore } from '../stores/channels.query-store'

import type { ChannelRoles, IChannelMemberComposit } from '../types'

/**
 * Channel Members Composable - Orchestrates channel member data fetching and mutations
 * Provides a clean API for managing channel members
 *
 * @param groupId - Optional group ID. If not provided, uses the current group from context
 * @param channelId - Optional channel ID. If provided, mutations will use this channelId automatically
 *
 * @example
 * // Use with current group and specific channel
 * const channelMembers = useChannelMembers(undefined, 'channel-123')
 *
 * // Use with specific group and channel
 * const groupId = ref('group-123')
 * const channelId = ref('channel-123')
 * const channelMembers = useChannelMembers(groupId, channelId)
 *
 * // Get channel members (channelId from composable)
 * const membersQuery = channelMembers.getChannelMembers(roles, searchText)
 *
 * // Get suggestions for adding members (channelId from composable)
 * const suggestionsQuery = channelMembers.getSuggestions({ search: 'john' })
 *
 * // Add members to channel (channelId from composable)
 * await channelMembers.addMembers({ members: [fullMemberObject] })
 *
 * // Update member role (channelId from composable)
 * await channelMembers.updateRole({ contactId, role })
 *
 * // Remove member from channel (channelId from composable)
 * await channelMembers.remove({ contactId })
 */
export const useChannelMembers = (groupId?: MaybeRefOrGetter<string | undefined>, channelId?: MaybeRefOrGetter<string | undefined>) => {
  // If no groupId provided, get it from the current groups context
  if (!groupId) {
    const { store: groupStore } = useGroups()
    const currentGroupId = computed(() => groupStore.currentGroup?._id)

    if (!currentGroupId.value) {
      console.warn('[useChannelMembers] No groupId provided and no current group found')
    }

    groupId = currentGroupId
  }

  // Initialize query store
  const queryStore = useChannelsQueryStore(groupId)

  // ===== CHANNEL MEMBERS QUERY =====
  /**
   * Get channel members query for a specific channel
   * Returns infinite query with allData computed property that flattens all pages
   */
  const getChannelMembers = (
    roles?: MaybeRefOrGetter<ChannelRoles[] | undefined>,
    searchText?: MaybeRefOrGetter<string | undefined>,
    pageSize: MaybeRefOrGetter<number> = 10
  ) => {
    return queryStore.channelMembersQuery(channelId, roles, searchText, pageSize)
  }

  // ===== CHANNEL SUGGESTIONS QUERY =====
  /**
   * Get group members for channel suggestions
   * Returns users who can be added to a specific channel
   */
  const getSuggestions = (params?: MaybeRefOrGetter<{ limit?: number; skip?: number; search?: string }>) => {
    return queryStore.channelSuggestionsQuery(channelId, params)
  }

  // ===== MUTATION WRAPPERS =====
  /**
   * Add members to channel
   * Uses channelId from composable arguments
   * Accepts full member objects with all details (contactId, role, fullName, avatar, etc.)
   */
  const addMembers = async (data: {
    members: IChannelMemberComposit[] // Full member objects with contactId, role, and all other details
  }) => {
    const chId = toValue(channelId)
    if (!chId) {
      throw new Error('Channel ID is required for adding members')
    }
    return queryStore.addChannelMembersMutation.mutateAsync({
      members: data.members,
      channelId: chId,
    })
  }

  /**
   * Update member role in channel
   * Uses channelId from composable arguments
   */
  const updateRole = async (data: { contactId: string; role: ChannelRoles }) => {
    const chId = toValue(channelId)
    if (!chId) {
      throw new Error('Channel ID is required for updating member role')
    }
    return queryStore.updateMemberRoleMutation.mutateAsync({
      ...data,
      channelId: chId,
    })
  }

  /**
   * Remove member from channel
   * Uses channelId from composable arguments
   */
  const remove = async (data: { contactId: string }) => {
    const chId = toValue(channelId)
    if (!chId) {
      throw new Error('Channel ID is required for removing member')
    }
    return queryStore.removeMemberMutation.mutateAsync({
      ...data,
      channelId: chId,
    })
  }

  // ===== PUBLIC API =====
  return {
    // Channel members query
    getChannelMembers,

    // Channel suggestions query
    getSuggestions,

    // Mutations (wrapped to use channelId from composable)
    addMembers,
    updateRole,
    remove,
  }
}
